import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { PlayDemo } from "./pages/PlayDemo.js";
import { Register } from "./pages/Register.js";
import { Login } from "./pages/Login.js";
import { VerifyEmail } from "./pages/VerifyEmail.js";
import { Tournament } from "./pages/Tournament.js";
import { Tournaments } from "./pages/Tournaments.js";
import { Admin } from "./pages/Admin.js";
import { Rounds } from "./pages/Rounds.js";
import { Live } from "./pages/Live.js";
import { Account } from "./pages/Account.js";
import { Play } from "./pages/Play.js";
import { Join } from "./pages/Join.js";
import { EventRedirect } from "./pages/EventRedirect.js";
import { TmLink } from "./pages/TmLink.js";

interface Me {
  nickname: string;
  publicId: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  role?: "player" | "arbiter" | "admin";
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me");
      setMe(res.ok ? await res.json() : null);
    } catch {
      setMe(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        color: "#222",
        minHeight: "100vh",
        background: "#fff",
      }}
    >
      <header
        style={{
          borderBottom: "1px solid #e0e0e0",
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          gap: 24,
          height: 56,
        }}
      >
        <Link
          to="/"
          style={{
            fontWeight: 700,
            fontSize: 17,
            color: "#00695c",
            textDecoration: "none",
            letterSpacing: "-0.01em",
          }}
        >
          FMJD Play Online
        </Link>
        <nav style={{ display: "flex", gap: 4, flex: 1 }}>
          <Tab to="/tournaments">Tournaments</Tab>
          <Tab to="/play">Demo board</Tab>
        </nav>
        <UserMenu me={me} loaded={loaded} onChange={refresh} />
      </header>

      {me && !me.emailVerified && <UnverifiedBanner />}

      <main style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
        <Routes>
          <Route path="/" element={<Home me={me} />} />
          <Route path="/play" element={<PlayDemo />} />
          <Route path="/register" element={<Register onRegistered={refresh} />} />
          <Route path="/login" element={<Login onSignedIn={refresh} />} />
          <Route path="/verify/email" element={<VerifyEmail onVerified={refresh} />} />
          <Route path="/tournaments" element={<Tournaments />} />
          <Route path="/t/:slug" element={<Tournament signedIn={Boolean(me)} />} />
          <Route path="/t/:slug/admin" element={<Admin />} />
          <Route path="/t/:slug/rounds" element={<Rounds />} />
          <Route path="/t/:slug/live" element={<Live />} />
          <Route path="/e/:uid" element={<EventRedirect />} />
          <Route path="/admin/tm" element={<TmLink />} />
          <Route path="/account" element={<Account onChange={refresh} />} />
          <Route path="/account/phone" element={<Account onChange={refresh} />} />
          <Route path="/account/email" element={<Account onChange={refresh} />} />
          <Route path="/account/verify" element={<Account onChange={refresh} />} />
          <Route path="/account/fmjd" element={<Account onChange={refresh} />} />
          <Route path="/g/:publicId" element={<Play />} />
          <Route path="/join/:token" element={<Join onSignedIn={refresh} />} />
        </Routes>
      </main>
    </div>
  );
}

function UserMenu({
  me,
  loaded,
  onChange,
}: {
  me: Me | null;
  loaded: boolean;
  onChange: () => void;
}) {
  const navigate = useNavigate();

  // Render nothing until /api/auth/me has answered, so the header does not
  // flash "Sign in" for a player who is already signed in.
  if (!loaded) return <div style={{ width: 140 }} />;

  if (!me) {
    return (
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Link to="/login" style={headerLink}>
          Sign in
        </Link>
        <Link to="/register" style={headerButton}>
          Register
        </Link>
      </div>
    );
  }

  async function signOut() {
    await fetch("/api/auth/logout", { method: "POST" }).catch(() => {});
    onChange();
    navigate("/");
  }

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", fontSize: 13 }}>
      <Link to="/account" style={{ fontWeight: 600, color: "#00695c", textDecoration: "none" }}>
        {me.nickname}
      </Link>
      <button onClick={signOut} style={signOutButton}>
        Sign out
      </button>
    </div>
  );
}

function UnverifiedBanner() {
  const [sent, setSent] = useState<string | null>(null);

  async function resend() {
    const res = await fetch("/api/verify/email/resend", { method: "POST" });
    const data = await res.json().catch(() => ({}));
    setSent(
      res.ok
        ? "Confirmation email sent."
        : data.retryAfterS
          ? `Please wait ${data.retryAfterS}s before requesting another.`
          : "Could not send, please try again later."
    );
  }

  return (
    <div
      style={{
        background: "#fff8e1",
        borderBottom: "1px solid #ffe082",
        padding: "10px 24px",
        fontSize: 14,
      }}
    >
      Your email address is not confirmed yet.{" "}
      {sent ?? (
        <button onClick={resend} style={inlineButton}>
          Send the confirmation email again
        </button>
      )}
    </div>
  );
}

function Tab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        padding: "6px 12px",
        borderRadius: 6,
        fontSize: 14,
        textDecoration: "none",
        color: isActive ? "#00695c" : "#555",
        background: isActive ? "#e0f2f1" : "transparent",
        fontWeight: isActive ? 600 : 400,
      })}
    >
      {children}
    </NavLink>
  );
}

function Home({ me }: { me: Me | null }) {
  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={{ marginTop: 0, fontSize: 28 }}>FMJD Play Online</h1>
      <p style={{ fontSize: 16, color: "#555", lineHeight: 1.6 }}>
        A game and tournament platform for the Fédération Mondiale du Jeu de Dames.
        Under development.
      </p>

      {me && (
        <div
          style={{
            marginTop: 20,
            padding: "12px 16px",
            background: "#e0f2f1",
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          Signed in as <strong>{me.nickname}</strong>
          {me.emailVerified && " — email confirmed"}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 16,
          marginTop: 28,
        }}
      >
        <Card
          title="Rules engine"
          state="working"
          body="International 10×10 and Brazilian 8×8. Move generation verified against published reference values to depth 7 — 1,049,442 positions."
          link={{ to: "/play", label: "Open the demo board" }}
        />
        <Card
          title="Accounts"
          state="working"
          body="Register once, enter any tournament. Email confirmation is live; phone verification by Telegram or SMS comes next."
          link={me ? undefined : { to: "/register", label: "Create an account" }}
        />
        <Card
          title="Tournaments"
          state="working"
          body="Entry, a public participant list and arbiter management are live. Pairing, automatic rounds and standings come next."
          link={{ to: "/tournaments", label: "See tournaments" }}
        />
        <Card
          title="Live play"
          state="next"
          body="Server-authoritative clocks, reconnection, spectators, and a full arbiter console."
        />
      </div>
    </div>
  );
}

function Card({
  title,
  body,
  state,
  link,
}: {
  title: string;
  body: string;
  state: "working" | "next";
  link?: { to: string; label: string };
}) {
  return (
    <div
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        padding: 16,
        background: state === "working" ? "#fff" : "#fafafa",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 15 }}>{title}</strong>
        <span
          style={{
            fontSize: 11,
            padding: "2px 7px",
            borderRadius: 10,
            background: state === "working" ? "#e8f5e9" : "#eeeeee",
            color: state === "working" ? "#2e7d32" : "#757575",
            fontWeight: 600,
          }}
        >
          {state === "working" ? "working" : "next"}
        </span>
      </div>
      <p style={{ fontSize: 13.5, color: "#555", lineHeight: 1.55, margin: "0 0 10px" }}>{body}</p>
      {link && (
        <Link to={link.to} style={{ fontSize: 13, color: "#00695c", fontWeight: 600 }}>
          {link.label} →
        </Link>
      )}
    </div>
  );
}

const headerLink: React.CSSProperties = {
  fontSize: 14,
  color: "#00695c",
  textDecoration: "none",
  fontWeight: 600,
  padding: "6px 10px",
};

const headerButton: React.CSSProperties = {
  fontSize: 14,
  color: "#fff",
  background: "#00695c",
  textDecoration: "none",
  fontWeight: 600,
  padding: "7px 14px",
  borderRadius: 6,
};

const signOutButton: React.CSSProperties = {
  fontSize: 13,
  padding: "5px 12px",
  border: "1px solid #ccc",
  borderRadius: 6,
  background: "#fff",
  cursor: "pointer",
};

const inlineButton: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#00695c",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
  textDecoration: "underline",
};
